import { ReactNode } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import logoMark from '../../../assets/ardealy-mark.svg';
import { useThemeMode } from '../hooks';

interface AuthLeftPanelProps {
  children: ReactNode;
  /** Left out by pages that draw their own heading inside the form (the emailed-link pages). */
  title?: string;
  subtitle?: string;
  /** Plays the leaving animation (register fades out before its overlay). */
  isFading?: boolean;
  fadeClassName: string;
  /** The line under the form that sends people to the other page; left out where there is none. */
  switchText?: string;
  switchLinkText?: string;
  switchLinkHref?: string;
}

export const AuthLeftPanel = ({
  children,
  title,
  subtitle,
  isFading,
  fadeClassName,
  switchText,
  switchLinkText,
  switchLinkHref,
}: AuthLeftPanelProps) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleColorMode } = useThemeMode();
  const themeLabel = isDarkMode ? t('AUTH.LIGHT_MODE') : t('AUTH.DARK_MODE');

  return (
    /*
     * `data-fading` drives the leaving animation (see index.css). The caller's
     * own `fadeClassName` is still applied — login and register use it as a
     * handle for their cross-fade between the two pages.
     */
    <div
      className={`${fadeClassName} flex min-h-full flex-col`}
      data-fading={isFading || undefined}
    >
      <div className="flex justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={toggleColorMode} aria-label={themeLabel}>
              {isDarkMode ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{themeLabel}</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center py-10">
        <div className="w-full max-w-[360px]">
          <div className="animate-avatar mb-5 flex justify-center">
            <img src={logoMark} alt="Ardealy" className="size-9 object-contain brightness-0 dark:invert" />
          </div>

          {title && (
            <h1 className="animate-form-item animate-delay-1 font-heading text-center text-2xl font-medium tracking-tight">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="animate-form-item animate-delay-1 mt-1.5 text-center text-sm text-muted-foreground">
              {subtitle}
            </p>
          )}

          <div className={title ? 'mt-8' : undefined}>{children}</div>

          {switchLinkHref && (
            <p className="animate-form-item animate-delay-5 mt-6 text-center text-sm text-muted-foreground">
              {switchText}{' '}
              <Link to={switchLinkHref} className="font-medium text-foreground hover:underline">
                {switchLinkText}
              </Link>
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-1 text-center text-xs text-muted-foreground">
        <span>
          {t('AUTH.HELP')}{' '}
          <a href="mailto:hello@ardealy.com" className="text-foreground hover:underline">
            hello@ardealy.com
          </a>
        </span>
        <span>© {new Date().getFullYear()} Ardealy</span>
      </div>
    </div>
  );
};
